import React, {useEffect, useState} from "react";
import Modal from "../../components/modal/modal.component";
import Form from "../../components/form";
import {StyledModalContent} from "../../components/modal/modal.style";
import {StyledFooter} from "../../components/pane/pane.style";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import ExactPhoneInput from "../../components/form/input/phoneInput.component";
import {updateInlineFieldValue, updatePhoneFieldValue} from "../../services/profile.service";
import {logout, useAuth} from "../../services/auth.service";
import {Links} from "../../app/links";
import {useHistory} from 'react-router-dom';
import {StyledWiteButton} from "../../components/button/button.style";
import LogoffImage from "../../assets/images/log-out.svg";
import {updateStorage} from "../../utils/tools/storage";
import {disableSwipeToClose} from "../../utils/tools/modals";
import SignModal from "@modals/sign/sign.component";
import SaveConfirmationModal from "@app/myData/modals/saveConfirmationModal.component";

type TeamMemberPhoneModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (phone: string) => void;
};

const TeamMemberPhoneModal: React.FC<TeamMemberPhoneModalProps> = (props: TeamMemberPhoneModalProps) => {
    const {t} = useTranslation();
    const history = useHistory();
    const auth = useAuth();

    const [show, updateShow] = useState<boolean>(false);
    const [phoneNumber, updatePhoneNumber] = useState<string>('48 ');
    const [missingPhone, setMissingPhone] = useState(!!phoneNumber);
    const [err, setErr] = useState('');
    const [startSaving, setStartSaving] = useState(false);
    const [showSignModal, setShowSignModal] = useState<boolean>(false);
    const [showSaveConfirmationModal, setShowSaveConfirmationModal] = useState<boolean>(false);

    const {handleSubmit, formState} = useForm({
        mode: 'onChange'
    });

    const {isValid} = formState;

    useEffect(() => {
        handleOpen();
    });

    const handleClose = () => {
        props.onClose();
    };

    const handleOpen = () => {
        updateShow(props.isOpen);
    };

    const handleSave = async () => {
        await updateInlineFieldValue('phone', phoneNumber)
            .then(response => {
                setShowSignModal(true);
            })
            .catch(error => {
                if (error.response.status === 404 && error.response.data.message == "WORKER_PHONE_DUPLICATE") {
                    if (error.response.data.phoneForContact) {
                        setErr(t("profileTab.personalData.validation.phoneDuplicateContact", {phone: error.response.data.phoneForContact}));
                    } else {
                        setErr(t("profileTab.personalData.validation.phoneDuplicate"));
                    }
                    setStartSaving(true);
                }
            })
    };

    const signSave = async (code: string) => {
        await updatePhoneFieldValue(phoneNumber, code)
            .then(response => {
                const phoneCodeParts = (phoneNumber || '').split(' ');

                updateStorage('phone_code', phoneCodeParts[0]);
                updateStorage('phone', phoneCodeParts[1]);

                props.onSubmit(phoneNumber);
                props.onClose();

                setShowSignModal(false);
            })
            .catch(error => {
                if (error.response && error.response.status === 400) {
                    setErr(t("common.invalidVerifyCode"));
                    setStartSaving(true);
                }
            });
    };


    const logoutClick = async () => {
        await logout().then(() => {
            auth.signOut();
            history.replace(Links.login)
            window.top.location.reload();
        });
    }

    return (
        <Modal
            isOpen={show}
            canDismiss={disableSwipeToClose}
            disableClose={true}
            onDidDismiss={() => handleClose()}>
            <Form.Container className="modalForm" onSubmit={handleSubmit(() => setShowSaveConfirmationModal(true))}>
                <StyledModalContent>
                    <h1 className="title">{t("teamMemberPhoneModal.title")}</h1>
                    <div className="content">
                        <p>{t("teamMemberPhoneModal.subtitle")}</p>
                        <ExactPhoneInput
                            phoneNumer={phoneNumber}
                            phoneNumerChanged={(phoneNumber) => {
                                updatePhoneNumber(phoneNumber);
                            }}
                            errorChanged={setMissingPhone}
                        ></ExactPhoneInput>
                        {startSaving && err && <p className="errorMessage">{err}</p>}
                    </div>
                    <StyledFooter className="modal footer no-shadow">
                        <Form.Button type="submit"
                                     disabled={!isValid || missingPhone}>{t("teamMemberPhoneModal.submit")}</Form.Button>
                        <StyledWiteButton onClick={e => logoutClick()}>{t("settingsTab.logoff")} <img
                            src={LogoffImage}/></StyledWiteButton>
                    </StyledFooter>
                </StyledModalContent>
            </Form.Container>
            <SaveConfirmationModal
                isOpen={showSaveConfirmationModal}
                onConfirm={() => handleSave()}
                onCancel={(() => setShowSaveConfirmationModal(false))}>
            </SaveConfirmationModal>
            <SignModal
                configuration={{
                    title: t("personalDataPhonePage.sign.title"),
                    info: t("personalDataPhonePage.sign.info"),
                    buttonText: t("personalDataPhonePage.sign.signButton"),
                    phone: phoneNumber,
                }}
                isOpen={showSignModal}
                onClose={() => setShowSignModal(false)}
                onSave={(code: string) => signSave(code)}
            >
            </SignModal>
        </Modal>
    )
}

export default TeamMemberPhoneModal;